import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getConsentSnapshot,
  isGlobalPrivacyControlEnabled,
  onConsentChange,
  setConsent,
  updateConsent,
  type ConsentCategory,
  type ConsentSnapshot,
} from '../../utils/consent';
import { isAnalyticsBlockedPath, onGaRouteChange } from '../../utils/gaGuard';

/**
 * GA4-02, then GA4-14 — the consent surface. In-house, no CMP, denied by
 * default, and since GA4-14 it asks two questions instead of one.
 *
 * It is the only thing standing between a visitor and GA4: nothing about the
 * tag — not the loader, not the inline bootstrap, not one `gtag()` call —
 * happens until somebody chooses here. utils/consent.ts stores the answer;
 * utils/gaGuard.ts acts on it. This file is only the surface.
 *
 * ─── THE HYDRATION RULE ──────────────────────────────────────────────────────
 * Mounted from `src/pages/+Layout.tsx`, which is server-rendered for every Vike
 * page. Consent lives in localStorage, so the server cannot know it and neither
 * can the first client render — React 19 compares the two and throws the whole
 * subtree away on a mismatch. `visible` therefore starts false and only an
 * effect can turn it on, the same shape src/components/MobileAppPrompt.tsx and
 * src/components/home/useClientOnlyStorage.ts use. The settings panel starts
 * closed for the same reason.
 *
 * ─── WHERE IT MUST NOT APPEAR ────────────────────────────────────────────────
 * The transient and credential-carrying surfaces: /auth/callback, the
 * password-reset route, and the admin screens. That list is exactly
 * `isAnalyticsBlockedPath` — the routes where no hit may be sent are the routes
 * where there is nothing to ask about, and reusing the predicate means the two
 * cannot drift apart. Asking on a screen that redirects itself a second later
 * would also be a consent dialog nobody can answer.
 *
 * Every exit from those routes is a CLIENT-SIDE navigation in this app (see the
 * routing note on `ensureGtagBootstrapped` in utils/gaGuard.ts), so eligibility
 * is re-evaluated on `onGaRouteChange` rather than only at mount. Without that,
 * a visitor who arrives on the email-verification screen would never be asked
 * for the rest of the visit.
 *
 * GA4-14 — AND IT IS STILL NOT GATED ON THE ADS-ELIGIBLE ALLOWLIST. The tag
 * layer will restrict where advertising tags may FIRE to a public-marketing
 * allowlist. That is a different question from where the visitor may be ASKED,
 * and conflating them would mean somebody who lands on a non-eligible page is
 * never offered the advertising choice and can therefore never grant it (recon
 * R9). Banner eligibility stays the blocklist, and only the blocklist.
 *
 * ─── STACKING, AND THE GROWTH-01 BANNER ──────────────────────────────────────
 * `z-[500]` and marked `data-app-banner-anchor`. Two separate problems:
 *
 *   OVERLAP is solved by the anchor. src/components/MobileAppPrompt.tsx already
 *   measures every `[data-app-banner-anchor]` on screen and seats itself above
 *   the tallest one — that is how it clears the property page's sticky price bar
 *   and BottomNav. Wearing the same mark makes the app banner stack on top of
 *   this one instead of landing on it, with no change to GROWTH-01 at all.
 *
 *   PRIORITY is solved by the z-index. If anything does overlap anyway, the
 *   consent gate wins: above the app banner and the sticky CTA (z-40), above the
 *   modals (z-50), and above Leaflet's controls (z-[400]), which is the only
 *   thing in this app that outranks a modal. A legal gate that another surface
 *   can cover is not a gate, and the reverse mistake — a banner nobody can
 *   dismiss because a map control sits on the Accept button — is worse than a
 *   bar that briefly overlaps a dialog. The settings panel sits one step higher
 *   again, because it is opened FROM the banner and must not be covered by it.
 */

const HEADING_ID = 'consent-banner-heading';
const PANEL_HEADING_ID = 'consent-panel-heading';

/**
 * THE LOCKED COPY, in one place.
 *
 * Every string the visitor reads while deciding, held as data rather than
 * inlined in the JSX, so that pages/__tests__/consentMode.test.ts can pin it
 * against the wording the item froze without scraping this file's markup. The
 * body is split around the link because the link is inside the sentence — not
 * because the sentence is two sentences.
 *
 * Approved by Boru Hussein Wario 2026-08-28 and recorded on the GA4-14 Notion
 * page. Do not reword any of it here. It is the text the consent was given
 * against, and changing it changes what was consented to.
 *
 * ONE EXCEPTION, MARKED. `gpcLockedExplanation` is Brain-drafted and carries
 * PENDING-LEGAL: it ships as written and may be swapped in a follow-up PR once
 * counsel has seen it. It describes a browser signal rather than our own
 * processing, which is why it was safe to ship ahead of the review.
 */
export const CONSENT_COPY = {
  heading: 'Your cookie choices',
  bodyBeforeLink:
    'We use cookies to keep Afriqahome working and, with your permission, to understand how the ' +
    'site is used and to show you Afriqahome ads on Google and Meta. You choose. Read our ',
  privacyLinkLabel: 'Privacy Policy',
  privacyHref: '/privacy',
  bodyAfterLink: '.',
  rejectAll: 'Reject all',
  acceptAll: 'Accept all',
  manageSettings: 'Manage choices',

  panelHeading: 'Manage choices',
  essentialLabel: 'Essential',
  essentialDescription: 'Always on. Sign-in, security, core features.',
  analyticsToggleLabel: 'Analytics',
  analyticsDescription:
    'Helps us see which pages and listings people use, so we can improve search and guides. ' +
    'Provider: Google Analytics 4.',
  advertisingToggleLabel: 'Advertising',
  advertisingDescription:
    "Lets us show Afriqahome's own ads to people who visited the site, on Google, Facebook and " +
    'Instagram. Never uses your messages, saved homes or search terms. Providers: Google Ads, Meta.',
  saveChoices: 'Save choices',

  cookieSettings: 'Cookie settings',
  doNotSell: 'Do Not Sell or Share My Personal Information',

  reconsentNotice:
    "We've updated how we use cookies — advertising is now a separate choice. Please review your " +
    'settings.',
  // PENDING-LEGAL — see the note above.
  gpcLockedExplanation:
    'Your browser has sent a Global Privacy Control signal, so advertising cookies stay off.',
} as const;

/**
 * Eligible = they have not answered EVERYTHING, and this is a surface worth
 * asking on.
 *
 * Either category being 'unset' is enough. A visitor whose analytics answer is
 * still current but whose advertising answer has never been given — every v1
 * record on the internet, after the version bump — has not finished answering,
 * and a banner that only appeared when BOTH were unset would silently treat
 * "never asked about advertising" as a refusal.
 */
function shouldPrompt(snapshot: ConsentSnapshot, pathname: string): boolean {
  const unanswered = snapshot.analytics === 'unset' || snapshot.advertising === 'unset';
  return unanswered && !isAnalyticsBlockedPath(pathname);
}

/**
 * Identical geometry and weight on the two primary buttons — same height, same
 * padding, same border width, same type. The locked decision carried forward
 * from GA4-02 is "equal visual prominence", and under IMY's April 2025
 * enforcement line that is not cosmetic: Reject is not a link, not smaller, not
 * greyed out, and not second-class. Only the fill colour differs, and both
 * carry a 2px border so neither reads as the quiet one.
 *
 * "Manage choices" is deliberately NOT one of them. It is not a third answer —
 * it opens a surface where the answer is given — so making it look like a
 * button of equal weight would present three options where there are two.
 */
const BUTTON_BASE =
  'min-h-[44px] flex-1 rounded-lg border-2 px-5 text-[14px] font-semibold transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary ' +
  'focus-visible:ring-offset-2 sm:flex-none sm:min-w-[128px]';

const LINK_ACTION =
  'rounded text-[13px] font-medium underline underline-offset-2 text-gray-600 ' +
  'hover:text-brand-primary focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-brand-primary';

/**
 * How the footers reach the panel.
 *
 * `CookieSettingsLink` and `DoNotSellOrShareLink` render inside the two footers,
 * which are nowhere near this component in the tree — so opening the panel is a
 * published event rather than a prop or a context. The same shape
 * utils/consent.ts already uses for consent changes, and for the same reason:
 * the alternative is threading a callback through every page that renders a
 * footer.
 */
type PanelFocus = ConsentCategory | null;
type PanelListener = (focus: PanelFocus) => void;

const panelListeners = new Set<PanelListener>();

function openConsentSettings(focus: PanelFocus = null): void {
  for (const listener of panelListeners) listener(focus);
}

function onOpenConsentSettings(listener: PanelListener): () => void {
  panelListeners.add(listener);
  return () => {
    panelListeners.delete(listener);
  };
}

/** Everything focusable inside the dialog, in DOM order — the trap's universe. */
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ConsentToggle: React.FC<{
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  note?: string;
  onChange: (next: boolean) => void;
}> = ({ id, label, description, checked, disabled = false, note, onChange }) => (
  <div className="flex items-start justify-between gap-4 border-t border-brand-border py-4">
    <div className="min-w-0">
      <p id={`${id}-label`} className="text-[14px] font-semibold text-brand-text">
        {label}
      </p>
      <p id={`${id}-description`} className="mt-1 text-[13px] leading-snug text-gray-600">
        {description}
      </p>
      {note && <p className="mt-1 text-[12px] leading-snug text-gray-500">{note}</p>}
    </div>
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-labelledby={`${id}-label`}
      aria-describedby={`${id}-description`}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={
        'relative mt-0.5 h-6 w-11 shrink-0 rounded-full border-2 transition-colors ' +
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary ' +
        'focus-visible:ring-offset-2 ' +
        (disabled
          ? 'cursor-not-allowed border-gray-300 bg-gray-200'
          : checked
            ? 'border-brand-primary bg-brand-primary'
            : 'border-gray-400 bg-white')
      }
    >
      <span
        aria-hidden="true"
        className={
          'absolute top-0.5 h-4 w-4 rounded-full transition-transform ' +
          (checked ? 'translate-x-[22px] bg-white' : 'translate-x-0.5 bg-gray-500')
        }
      />
    </button>
  </div>
);

const ConsentBanner: React.FC = () => {
  const [snapshot, setSnapshot] = useState<ConsentSnapshot | null>(null);
  const [visible, setVisible] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [gpc, setGpc] = useState(false);

  // The panel's working copy. Nothing is written until "Save choices", so
  // closing without saving changes nothing — which is what makes the panel safe
  // to open from a footer link on a page the visitor is only passing through.
  const [draftAnalytics, setDraftAnalytics] = useState(false);
  const [draftAdvertising, setDraftAdvertising] = useState(false);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  const pendingFocus = useRef<PanelFocus>(null);

  const evaluate = useCallback(() => {
    const next = getConsentSnapshot();
    setSnapshot(next);
    setVisible(shouldPrompt(next, window.location.pathname));
    setGpc(isGlobalPrivacyControlEnabled());
  }, []);

  useEffect(() => {
    evaluate();

    // Three reasons the answer can change under us: somebody decided (here, in
    // the panel, in another tab, or by a category ageing out), the app moved to
    // a different route, or a footer link asked for the panel.
    const unsubscribeConsent = onConsentChange(evaluate);
    const unsubscribeRoute = onGaRouteChange(evaluate);
    const unsubscribePanel = onOpenConsentSettings((focus) => {
      // Seed the toggles from the CURRENT record, not from off. A visitor who
      // already granted analytics and opens the panel to turn advertising off
      // must not have analytics silently revoked by the same Save.
      const current = getConsentSnapshot();
      setDraftAnalytics(current.analytics === 'granted');
      setDraftAdvertising(current.advertising === 'granted');
      pendingFocus.current = focus;
      restoreFocusTo.current =
        typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
      setPanelOpen(true);
    });

    return () => {
      unsubscribeConsent();
      unsubscribeRoute();
      unsubscribePanel();
    };
  }, [evaluate]);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    // Back where they came from — the footer link, or the banner's "Manage
    // choices" — rather than to the top of the document.
    const target = restoreFocusTo.current;
    restoreFocusTo.current = null;
    if (target && typeof target.focus === 'function') target.focus();
  }, []);

  // Focus management and the trap. A dialog a keyboard user can tab out of,
  // into a page they cannot see, is not a modal.
  useEffect(() => {
    if (!panelOpen) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    const focusTarget =
      (pendingFocus.current === 'advertising'
        ? dialog.querySelector<HTMLElement>('#consent-toggle-advertising')
        : null) ?? dialog.querySelector<HTMLElement>(FOCUSABLE);
    focusTarget?.focus();
    pendingFocus.current = null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closePanel();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable: HTMLElement[] = Array.prototype.slice.call(
        dialog.querySelectorAll(FOCUSABLE),
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      // Wrap at both ends, and pull focus back in if it has escaped the dialog
      // entirely (a click on the backdrop, a browser-restored focus).
      if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [panelOpen, closePanel]);

  // Hiding is not done in these handlers: `setConsent` notifies, `evaluate`
  // runs, and the banner closes because neither category is 'unset' any more.
  // One path in and one path out means a decision made in another tab closes
  // this banner exactly the way a click does.
  const acceptAll = useCallback(
    () => setConsent({ analytics: 'granted', advertising: 'granted' }),
    [],
  );
  const rejectAll = useCallback(
    () => setConsent({ analytics: 'denied', advertising: 'denied' }),
    [],
  );

  const saveChoices = useCallback(() => {
    const current = getConsentSnapshot();
    const wanted = {
      analytics: draftAnalytics ? ('granted' as const) : ('denied' as const),
      advertising: draftAdvertising ? ('granted' as const) : ('denied' as const),
    };

    // CHANGED CATEGORIES ONLY. An untouched category keeps its stored timestamp
    // rather than being restamped, so pressing Save does not quietly renew a
    // consent the visitor did not revisit — see `updateConsent`. A category
    // that is still 'unset' always counts as changed: leaving a toggle off and
    // pressing Save is an answer, and it is "no".
    const partial: Partial<Record<ConsentCategory, 'granted' | 'denied'>> = {};
    if (current.analytics !== wanted.analytics) partial.analytics = wanted.analytics;
    if (current.advertising !== wanted.advertising) partial.advertising = wanted.advertising;

    if (Object.keys(partial).length > 0) updateConsent(partial);
    closePanel();
  }, [draftAnalytics, draftAdvertising, closePanel]);

  if (!visible && !panelOpen) return null;

  return (
    <>
      {visible && (
        <div
          data-app-banner-anchor
          role="region"
          aria-labelledby={HEADING_ID}
          /* A sheet on a phone, a bar on a desktop — the rounded top edge is the
             only difference, and it is the one that makes a full-width block at
             the bottom of a small screen read as a sheet rather than as chrome.
             No shadow: the Brand Style Block expresses elevation with fills and
             borders, so this sits on a border-top like every other surface. */
          className="fixed inset-x-0 bottom-0 z-[500] rounded-t-2xl border-t border-brand-border bg-white print:hidden sm:rounded-none"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:gap-8 sm:px-6">
            <div className="min-w-0 flex-1">
              <h2 id={HEADING_ID} className="text-[15px] font-bold leading-tight text-brand-text">
                {CONSENT_COPY.heading}
              </h2>

              {/* Shown ONLY to somebody whose earlier record this build retired.
                  A first-time visitor is not told we changed something they
                  never saw, and a returning one is not greeted as a stranger
                  after being re-asked for reasons of our own. */}
              {snapshot?.superseded && (
                <p className="mt-1 text-[13px] font-medium leading-snug text-brand-text">
                  {CONSENT_COPY.reconsentNotice}
                </p>
              )}

              <p className="mt-1 text-[13px] leading-snug text-gray-600">
                {CONSENT_COPY.bodyBeforeLink}
                <a
                  href={CONSENT_COPY.privacyHref}
                  className="rounded underline underline-offset-2 hover:text-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                >
                  {CONSENT_COPY.privacyLinkLabel}
                </a>
                {CONSENT_COPY.bodyAfterLink}
              </p>
            </div>

            <div className="flex shrink-0 flex-col gap-2">
              <div className="flex gap-3">
                {/* Reject first, in the approved order. Both carry BUTTON_BASE:
                    same box, same weight, no default action. */}
                <button
                  type="button"
                  onClick={rejectAll}
                  className={`${BUTTON_BASE} border-brand-text bg-white text-brand-text hover:bg-brand-bg`}
                >
                  {CONSENT_COPY.rejectAll}
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className={`${BUTTON_BASE} border-brand-primary bg-brand-primary text-white hover:bg-brand-primary/90`}
                >
                  {CONSENT_COPY.acceptAll}
                </button>
              </div>
              <button
                type="button"
                onClick={() => openConsentSettings(null)}
                className={`${LINK_ACTION} self-center sm:self-end`}
              >
                {CONSENT_COPY.manageSettings}
              </button>
            </div>
          </div>
        </div>
      )}

      {panelOpen && (
        <div
          className="fixed inset-0 z-[510] flex items-end justify-center bg-black/40 p-0 print:hidden sm:items-center sm:p-4"
          /* The backdrop closes, like every other dismissible layer in this app.
             Esc does the same from the keydown handler above. Neither saves —
             the only writer is "Save choices". */
          onClick={closePanel}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={PANEL_HEADING_ID}
            onClick={(event) => event.stopPropagation()}
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-brand-border bg-white px-5 py-5 sm:rounded-2xl"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1.25rem)' }}
          >
            <h2
              id={PANEL_HEADING_ID}
              className="text-[17px] font-bold leading-tight text-brand-text"
            >
              {CONSENT_COPY.panelHeading}
            </h2>

            {/* Display-only. Essential cookies are not consented to — they are
                what the site is, and offering a switch that cannot move is
                worse than stating the fact plainly. No role="switch" here for
                exactly that reason: nothing for a screen reader to try. */}
            <div className="mt-4 flex items-start justify-between gap-4 border-t border-brand-border pt-4">
              <div className="min-w-0">
                <p className="text-[14px] font-semibold text-brand-text">
                  {CONSENT_COPY.essentialLabel}
                </p>
                <p className="mt-1 text-[13px] leading-snug text-gray-600">
                  {CONSENT_COPY.essentialDescription}
                </p>
              </div>
            </div>

            <ConsentToggle
              id="consent-toggle-analytics"
              label={CONSENT_COPY.analyticsToggleLabel}
              description={CONSENT_COPY.analyticsDescription}
              checked={draftAnalytics}
              onChange={setDraftAnalytics}
            />

            {/* D2: while GPC is on, this cannot be turned on at all — the
                toggle is locked off and says why. utils/consent.ts coerces it
                on both read and write as well, so the lock is a statement of
                what the record will do rather than the thing enforcing it. */}
            <ConsentToggle
              id="consent-toggle-advertising"
              label={CONSENT_COPY.advertisingToggleLabel}
              description={CONSENT_COPY.advertisingDescription}
              checked={gpc ? false : draftAdvertising}
              disabled={gpc}
              note={gpc ? CONSENT_COPY.gpcLockedExplanation : undefined}
              onChange={setDraftAdvertising}
            />

            <div className="mt-5 flex justify-end border-t border-brand-border pt-4">
              <button
                type="button"
                onClick={saveChoices}
                className={`${BUTTON_BASE} border-brand-primary bg-brand-primary text-white hover:bg-brand-primary/90`}
              >
                {CONSENT_COPY.saveChoices}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsentBanner;

/**
 * The footer's re-consent surface.
 *
 * GA4-14 changed what this DOES while keeping what it is called and where it
 * lives. Under GA4-02 it called `clearConsent()`: the record was wiped and the
 * banner reopened, which was defensible while there was one category to
 * re-answer. With two it is not — a visitor who wants advertising off would
 * have to re-answer analytics as well, and analytics would go cold in between.
 * GDPR Art. 7(3) asks that withdrawal be as easy as consent, so this opens the
 * per-category panel instead (PO decision D4) and `clearConsent()` now has no
 * UI caller at all.
 *
 * A `<button>`, not an `<a>`: it performs an action and goes nowhere. It lives
 * here rather than in either footer so that the two footers this site ships
 * (src/components/FooterSSR.tsx everywhere, src/components/home/HomeFooter.tsx
 * on Home) cannot offer different behaviour under the same label.
 */
export const CookieSettingsLink: React.FC<{ className?: string }> = ({ className }) => (
  <button type="button" onClick={() => openConsentSettings(null)} className={className}>
    {CONSENT_COPY.cookieSettings}
  </button>
);

/**
 * The CCPA/CPRA opt-out link (PO decision D2).
 *
 * Google Ads remarketing and a Meta Pixel are the textbook definition of
 * "sharing personal information for cross-context behavioural advertising", and
 * CPRA requires a link by this exact name — not a disclosure, a LINK that
 * reaches the control. The locked global scope means US visitors are in, so it
 * ships on every page of the site rather than behind a region check.
 *
 * Same panel as "Cookie settings", opened with focus on the Advertising toggle,
 * because a visitor who followed a link about selling and sharing should not
 * have to hunt for the row it refers to.
 */
export const DoNotSellOrShareLink: React.FC<{ className?: string }> = ({ className }) => (
  <button type="button" onClick={() => openConsentSettings('advertising')} className={className}>
    {CONSENT_COPY.doNotSell}
  </button>
);
